import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTemplate } from '../../../../features/template/TemplateProvider';
import { ButtonCancel, ButtonPrimary } from '../../../../utils/Button';
import cn from '../../../../utils/cn';
import FocusControl from '../../../../utils/FocusControl';
import InputField, {
    InputCheckbox,
    InputDateField,
    SelectOption,
    addOption
} from '../../../../utils/InputField';
import { mkRelativeUrl } from '../../../../utils/URL';
import { ApiGetActiveProductListOpe } from '../../../Settings/ProductList/ProductOperation';
import { handleNewAddSubscriptionOpe } from '../../../Subscriptions/SubscriptionsOperation';
import { ApiGetPricingServiceList, handleLoadPricing } from '../CustomerDetailsOperation';
import AppServiceList from './Comp/AppServiceList';
import PackageInfo from './Comp/PackageInfo';

const billingCycles = [
    { key: 1, value: 'monthly', text: 'Monthly' },
    { key: 3, value: 'quarterly', text: 'Quarterly' },
    { key: 6, value: 'half-yearly', text: 'Half-Yearly' },
    { key: 12, value: 'yearly', text: 'Yearly' }
];

function AddNewSubscriptions({ data, closeModal }) {
    const navigate = useNavigate();
    const { setMessage } = useTemplate();
    const [isError, setIsError] = useState('');
    const [loading, setLoading] = useState(0); // 1 = submit loading, 2 = pricing loading, 3 = service loading
    const [applicationList, setApplicationList] = useState([]); // Initial Application List
    const [applicationPackageList, setApplicationPackageList] = useState([]); // Application Package List
    const [appServicesList, setAppServicesList] = useState([]); // Initial Service List

    const [formData, setFormData] = useState({
        // ...iniState,
        UserId: data?.id,
        WarrantyStartDate: dayjs().format('YYYY-MM-DD'),
        ServiceRenewPercent: 0,
        ApplicationId: 0,
        SubscriptionName: '',
        DomainName: '',
        PackageId: 0,
        PriceId: 0,
        Status: 'active',
        ServicePackageData: [],
        WarrantyDuration: 0,
        BillingCycle: 0,
        PackagePrice: 0,
        IsQuotation: false
    });

    // Set Form Data on change
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name) {
            setFormData((d) => {
                if (name === 'ServiceRenewPercent') {
                    // check regex number only with 2 decimal point and min 0 max 100
                    if (
                        !/^(100(\.0{1,2})?|[0-9]{1,2}(\.[0-9]{1,2})?)$/.test(value) &&
                        value !== ''
                    ) {
                        return d;
                    }
                    return { ...d, ServiceRenewPercent: value };
                }
                return { ...d, [name]: value };
            });
        }
    };

    // Set Application ID
    const handelSetApplicationId = (e) => {
        const { value } = e.target;

        const subscriptionName = applicationList.find(
            (item) => item?.id?.toString() === value
        )?.applicationTitle;

        setAppServicesList([]);

        setFormData((d) => ({
            ...d,
            ApplicationId: value,
            PackageId: 0,
            PriceId: 0,
            PackagePrice: 0,
            ServiceRenewPercent: 0,
            ServicePackageData: [],
            SubscriptionName: value ? `${subscriptionName} - ${data?.organization}` : ''
        }));
    };
    // Set Package Id
    const handelSetApplicationPackageId = (item) => {
        setFormData((d) => ({
            ...d,
            PackageId: item?.packageId,
            PriceId: item?.priceId,
            PackagePrice: item?.amount,
            ServiceRenewPercent: 0,
            ServicePackageData: []
        }));
    };

    // add new subscription handler
    const handleAddSubscription = (e) => {
        setLoading(1);
        e.preventDefault();
        handleNewAddSubscriptionOpe(formData, setIsError)
            .then(({ data: resData }) => {
                navigate(mkRelativeUrl(`/billing/invoices/${resData?.invoiceId}/info`));
            })
            .catch((error) => {
                setMessage({ type: 'error', text: error.toString(0) });
            })
            .finally(() => {
                setLoading(0);
            });
    };

    // Calculate after percent amount
    const afterPercentAmount = useMemo(() => {
        const percent = parseFloat(formData?.ServiceRenewPercent || 0);
        const packagePrice = parseFloat(formData?.PackagePrice || 0);
        const amount = (packagePrice * percent) / 100;
        return `${amount.toFixed(2)} ${data?.currency || ''}`;
    }, [
        formData?.ServiceRenewPercent,
        formData?.PackagePrice,
        data?.currency,
        formData?.ApplicationId
    ]);

    // --Load Application List [Load when change application ID]
    useEffect(() => {
        setFormData((d) => ({
            ...d,
            ApplicationId: 0,
            PackageId: 0,
            PriceId: 0,
            PackagePrice: 0,
            ServiceRenewPercent: 0,
            ServicePackageData: []
        }));
        ApiGetActiveProductListOpe()
            .then(({ data: resData }) => {
                setApplicationList(resData.applicationList);
            })
            .catch((error) => {
                setMessage({ type: 'error', text: error?.message });
            });
    }, [setMessage]);

    // Load Application Package & Pricing form API
    useEffect(() => {
        if (formData?.ApplicationId) {
            setLoading(2);
            handleLoadPricing(data?.id, formData?.ApplicationId)
                .then(({ data: resData }) => {
                    setApplicationPackageList(resData.packagesList);
                })
                .catch((error) => {
                    setMessage({ type: 'error', text: error.toString(0) });
                })
                .finally(() => setLoading(0));
        }

        // if no application id selected
        if (!formData?.ApplicationId) {
            setApplicationPackageList([]);
            setFormData((d) => ({
                ...d,
                PackageId: 0,
                PriceId: 0,
                ServicePackageData: [],
                PackagePrice: 0,
                ServiceRenewPercent: 0
            }));
        }
    }, [setMessage, formData?.ApplicationId, data?.id]);

    // Load services list
    useEffect(() => {
        // Fetch services list
        if (formData.UserId && formData.ApplicationId > 0 && formData.PackageId) {
            setLoading(3);
            setAppServicesList([]);
            ApiGetPricingServiceList(formData.UserId, formData.ApplicationId, formData.PackageId)
                .then(({ data: resData }) => {
                    setAppServicesList(resData.services);
                    setLoading(0);
                })
                .catch((err) => {
                    setLoading(0);
                    setMessage({ type: 'error', text: err.toString() });
                });
        } else {
            setAppServicesList([]);
        }
    }, [setMessage, formData.UserId, formData.ApplicationId, formData.PackageId]);

    // Focus Control
    FocusControl(isError, setIsError, formData);

    return (
        <form className="flex flex-col gap-4" onSubmit={handleAddSubscription}>
            <div className="flex gap-6 h-[500px]">
                <div className="flex flex-col gap-2 flex-1">
                    <SelectOption
                        label="Application"
                        name="ApplicationId"
                        formData={formData}
                        onChange={handelSetApplicationId}
                        options={applicationList?.map((item) =>
                            addOption(item?.id, item?.applicationTitle, item?.id)
                        )}
                        required
                        disabled={applicationList?.length === 0}
                    />
                    <InputField
                        label="Subscription Name"
                        placeholder="EX: Basic"
                        name="SubscriptionName"
                        onChange={handleChange}
                        formData={formData}
                        required
                    />
                    {/* Warranty Start Date */}
                    <div>
                        <InputDateField
                            label="Warranty Start Date"
                            name="WarrantyStartDate"
                            formData={formData}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    {/* Warranty Duration */}
                    <InputField
                        label="Warranty Duration (months)"
                        placeholder="EX: Enter your number in month"
                        name="WarrantyDuration"
                        onChange={handleChange}
                        formData={formData}
                        type="number"
                        min={0}
                        required
                    />

                    {/* Title */}
                    <div className="flex gap-2 w-full">
                        <div className="flex flex-col gap-1 flex-1">
                            <h1>
                                Service Agreement <span className="text-error">*</span>
                            </h1>
                            {/* <span>% of total value of products.</span> */}
                            <div className="flex justify-between pl-0 px-3 items-center gap-2 bg-primary-50 rounded-5 border overflow-hidden h-8.5 md:h-10">
                                <input
                                    type="text"
                                    className={cn(
                                        'w-20 pl-2 p-1 border-gray-300 focus:outline-none inline-block h-full',
                                        {
                                            'bg-primary-50': formData?.PackageId === 0
                                        }
                                    )}
                                    value={
                                        formData?.PackageId === 0
                                            ? 0
                                            : formData?.ServiceRenewPercent
                                    }
                                    onChange={handleChange}
                                    name="ServiceRenewPercent"
                                    disabled={
                                        formData?.ApplicationId === 0 || formData?.PackageId === 0
                                    }
                                    min={0}
                                />
                                <span>% of Year</span>
                            </div>
                            {/* Service Renew Price */}
                            <div className="text-sm text-secondary ">
                                Service Renew Price:&nbsp;
                                <span className="font-medium">{afterPercentAmount}</span>
                            </div>
                        </div>
                        <SelectOption
                            label="Billing Cycle"
                            name="BillingCycle"
                            formData={formData}
                            onChange={handleChange}
                            className="flex-1"
                            options={billingCycles.map((item) =>
                                addOption(item.value, item.text, item.key)
                            )}
                            required
                            // required if there is no service selected then disable inline condition
                            disabled={formData?.ApplicationId === 0 || formData?.PackageId === 0}
                        />
                    </div>
                    {/* Show total  */}
                    <InputField
                        label="Domain"
                        placeholder='EX: "www.example.com"'
                        name="DomainName"
                        onChange={handleChange}
                        formData={formData}
                        optional
                    />
                    <InputCheckbox
                        label="Bill To Agency"
                        name="IsQuotation"
                        formData={formData}
                        onChange={(e) =>
                            setFormData((prev) => ({ ...prev, IsQuotation: e.target.checked }))
                        }
                        text="Create as Quotation"
                        className="mt-1 " // [&>div>span]:text-warning"
                    />
                    <div className="text-sm text-error mt-2">
                        * If you check &quot;Create as Quotation&quot;, the invoice will be created
                        as a quotation and you can convert it to an invoice after approval.
                    </div>
                </div>
                <div className="flex-1 flex flex-col h-full">
                    <PackageInfo
                        applicationPackageList={applicationPackageList}
                        handelSetApplicationPackageId={handelSetApplicationPackageId}
                        formData={formData}
                        isLoading={loading === 2}
                        isSelectedApplication={formData?.ApplicationId > 0}
                    />

                    <AppServiceList
                        isLoading={loading === 3}
                        appServicesList={appServicesList}
                        formData={formData}
                        setFormData={setFormData}
                    />
                </div>
            </div>

            <div className="w-full flex justify-end items-center gap-2 mb-2">
                <ButtonCancel title="Cancel" onClick={() => closeModal(false)} />
                <ButtonPrimary
                    title="Create Invoice"
                    btnType="submit"
                    loading={loading === 1}
                    disabled={formData.ServiceRenewPercent === ''}
                />
            </div>
        </form>
    );
}

export default AddNewSubscriptions;
